import { type FileRef } from '@shared/session'
import { clsx } from 'clsx'
import { ArrowUp, FileText, Paperclip, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useColumn } from './chatLayout'
import { type DraftRef } from './store'
import { parseMaterialHref } from '@shared/material'
import { MaterialChip } from '@/materials/MaterialLink'
import { useBoardCtx } from '@/board/context'
import { uploadFile } from '@/board/upload'
import { useI18n } from '@/i18n/i18n'
import { isComposing } from '@/lib/ime'

/**
 * The chat input. Enter sends, Shift+Enter breaks the line (never while an IME
 * is composing). Files can be attached or pasted; they go to the AI with the
 * message and are not put on the board.
 */
export function ChatComposer({ disabled }: { disabled?: boolean }) {
  const column = useColumn()
  const { boardId, sessions } = useBoardCtx()
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  /** Places the message points at (from the reader's "ask"), shown as chips above the text. */
  const [pointers, setPointers] = useState<DraftRef[]>([])
  const [busy, setBusy] = useState(false)
  const area = useRef<HTMLTextAreaElement>(null)
  const picker = useRef<HTMLInputElement>(null)

  // A place sent here from elsewhere (the reader's "ask"): attached, then the input focused.
  useEffect(() => {
    let seen = sessions.getSnapshot().draft?.n ?? 0
    const off = sessions.subscribe(() => {
      const d = sessions.getSnapshot().draft
      if (!d || d.n === seen) return
      seen = d.n
      setPointers((cur) => (cur.some((p) => p.href === d.ref.href) ? cur : [...cur, d.ref]))
      requestAnimationFrame(() => {
        area.current?.focus()
      })
    })
    return () => {
      off()
    }
  }, [sessions])

  const grow = () => {
    const el = area.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }

  const send = async () => {
    // The places pointed at lead the message, as links (chips once sent).
    const lead = pointers.map((p) => `[${p.label}](${p.href})`).join(' ')
    const body = [lead, text.trim()].filter(Boolean).join(' ')
    if ((!body && !files.length) || busy) return
    setBusy(true)
    try {
      const refs: FileRef[] = []
      for (const f of files) {
        const up = await uploadFile(boardId, f)
        refs.push({ type: 'moot_file', fileKey: up.fileKey, mime: up.mime, name: up.name })
      }
      sessions.send(body, refs)
      setText('')
      setFiles([])
      setPointers([])
      requestAnimationFrame(grow)
    } catch {
      toast.error(t('upload.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex-none border-t border-line p-3">
      <div
        className={clsx(
          column,
          'rounded-lg border bg-bg transition-[border-color,box-shadow]',
          'border-line focus-within:border-(--focus) focus-within:shadow-[0_0_0_3px_var(--ai-soft)]',
        )}
      >
        {pointers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-2.5 pt-2.5">
            {pointers.map((p) => {
              const place = parseMaterialHref(p.href)
              return (
                <span key={p.href} className="inline-flex max-w-full items-center">
                  {place ? (
                    <MaterialChip target={place} fallback={p.label} />
                  ) : (
                    <span className="truncate text-[12px]">{p.label}</span>
                  )}
                  <button
                    type="button"
                    aria-label={t('chat.removePointer', { name: p.label })}
                    onClick={() => {
                      setPointers((cur) => cur.filter((x) => x.href !== p.href))
                    }}
                    className="ml-0.5 text-muted hover:text-ink"
                  >
                    <X size={12} />
                  </button>
                </span>
              )
            })}
          </div>
        )}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-2.5 pt-2.5">
            {files.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="inline-flex max-w-full items-center gap-1 rounded-md border border-line bg-surface px-1.5 py-0.5 text-[12px]"
              >
                <FileText size={12} className="flex-none text-muted" />
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  aria-label={t('chat.removeFile', { name: f.name })}
                  onClick={() => {
                    setFiles((cur) => cur.filter((_, j) => j !== i))
                  }}
                  className="text-muted hover:text-ink"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          ref={area}
          rows={1}
          value={text}
          disabled={disabled}
          aria-label={t('chat.placeholder')}
          placeholder={t('chat.placeholder')}
          onChange={(e) => {
            setText(e.target.value)
            grow()
          }}
          onPaste={(e) => {
            const pasted = [...e.clipboardData.files]
            if (pasted.length) {
              e.preventDefault()
              setFiles((cur) => [...cur, ...pasted])
            }
          }}
          onKeyDown={(e) => {
            if (isComposing(e)) return
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
          className="block max-h-[200px] w-full resize-none bg-transparent px-3 pt-2.5 pb-1 text-[13.5px] leading-relaxed outline-none placeholder:text-muted"
        />
        <div className="flex items-center gap-1 px-1.5 pb-1.5">
          <button
            type="button"
            aria-label={t('chat.attach')}
            title={t('chat.attach')}
            onClick={() => picker.current?.click()}
            className="grid size-8 place-items-center rounded-md text-muted hover:bg-surface-2 hover:text-ink"
          >
            <Paperclip size={16} />
          </button>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              const picked = [...(e.target.files ?? [])]
              e.target.value = ''
              setFiles((cur) => [...cur, ...picked])
            }}
          />
          <span className="flex-1" />
          <button
            type="button"
            aria-label={t('chat.send')}
            disabled={busy || (!text.trim() && !files.length && !pointers.length)}
            onClick={() => void send()}
            className="grid size-8 place-items-center rounded-md bg-ink text-bg transition-opacity disabled:opacity-30"
          >
            <ArrowUp size={16} strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </div>
  )
}
